import type { Margin } from '@oneaudi/unified-web-common';
import { Checkbox, TextLink } from '@oneaudi/unified-web-components';
import type { FC } from 'react';
import React, { useContext, useMemo } from 'react';
import styled from 'styled-components';
import { CompareContext } from '../../context/CompareContext';
import { createCompareCheckboxId } from '../../context/compareContextUtils';
import { useContent } from '../../context/ContentContext';
import { useI18n } from '../../i18n';

const SkipLink = styled(TextLink)`
  background-color: ${({ theme }) => theme.global.color.canvas.default};
  position: absolute;
  left: -9999px;

  &:focus {
    left: 0;
  }
`;

export type CompareCheckboxProps = {
  readonly carlineId: string;
  readonly carlineName: string;
};

export const CompareCheckbox: FC<CompareCheckboxProps> = ({ carlineId, carlineName }) => {
  const { compareMode } = useContent();
  const { state, dispatch } = useContext(CompareContext);
  const checked = useMemo(() => state.carlines.indexOf(carlineId) !== -1, [state]);
  const label = useI18n('compare.checkboxLabel');
  const ariaLabel = useI18n('compare.checkboxAriaLabel', {
    carlineName,
  });
  const skipToCompareText = useI18n('compare.skipToCompareText');
  const id = createCompareCheckboxId(carlineId);
  const margin: Margin | undefined =
    compareMode === 'on' ? [0, 0, 'var(--spacing-relative-lg)', 0] : undefined;

  return (
    <>
      <Checkbox
        id={id}
        name={id}
        label={label}
        aria-label={ariaLabel}
        value={carlineId}
        onChange={(e) => {
          if (e.target.checked === true) {
            dispatch({ type: 'add', carline: e.target.value });
          } else {
            dispatch({ type: 'remove', carline: e.target.value });
          }
        }}
        checked={checked}
        disabled={!checked && state.carlines.length >= state.amount}
        margin={margin}
      />
      {checked && (
        <SkipLink href="#compare-toggle" size="sm" variant="standalone">
          {skipToCompareText}
        </SkipLink>
      )}
    </>
  );
};
